import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './redux/store';
import axios from 'axios';
import { getCookie, setCookie } from './utils/cookieHandler';
import { config } from './utils/config';

// --- Proactive refresh when _AT is near expiry ---
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // 2 minute
let refreshPromise = null;
const refreshClient = axios.create(); // separate client to avoid interceptor loops

function getJwtExpMs(token) {
  try {
    if (!token) return null;
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const payload = JSON.parse(atob(base64));
    return payload && payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

async function doRefresh(trigger) {
  const rt = getCookie('_RT');
  if (!rt) throw new Error('Missing refresh token');
  console.log('[Auth] Refreshing access token...', trigger ? `(trigger=${trigger})` : '');
  const resp = await refreshClient.post(`${config.apiUrl}/api/authentication/refresh`, { refresh_token: rt }, { headers: { 'Content-Type': 'application/json' } });
  const newAT = resp?.data?.token;
  if (!newAT) throw new Error('No access token from refresh');
  setCookie('_AT', newAT);
  console.log('[Auth] Access token refreshed successfully');
  return newAT;
}

axios.interceptors.request.use(
  async (reqConfig) => {
    const urlStr = String(reqConfig && reqConfig.url ? reqConfig.url : '');
    // Skip proactive refresh for the refresh endpoint itself to avoid deadlocks
    if (urlStr.includes('/api/authentication/refresh')) {
      return reqConfig;
    }
    try {
      const currentAT = getCookie('_AT');
      const expMs = getJwtExpMs(currentAT);
      if (expMs && expMs - Date.now() <= REFRESH_THRESHOLD_MS) {
        if (!refreshPromise) refreshPromise = doRefresh('proactive').finally(() => (refreshPromise = null));
        await refreshPromise;
      }
    } catch (_) {}
    // Ensure Authorization header uses the latest _AT
    const latestAT = getCookie('_AT');
    if (latestAT) {
      reqConfig.headers = reqConfig.headers || {};
      reqConfig.headers['Authorization'] = `Bearer ${latestAT}`;
    }
    return reqConfig;
  },
  (error) => Promise.reject(error)
);

// Axios interceptor to refresh access token on 401
axios.interceptors.response.use(
  (response) => {
    try {
      const header = response?.headers?.authorization;
      if (header && header.startsWith('Bearer ')) {
        const token = header.split(' ')[1];
        if (token && `Bearer ${getCookie('_AT')}` !== header) {
          setCookie('_AT', token);
        }
      }
    } catch (_) {}
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const isUnauthorized = error.response && error.response.status === 401;
    const alreadyRetried = originalRequest.__rtRetried === true;
    if (isUnauthorized && !alreadyRetried) {
      try {
        originalRequest.__rtRetried = true;
        const refreshToken = getCookie('_RT');
        if (!refreshToken) throw new Error('Missing refresh token');

        console.log('[Auth] 401 detected, attempting token refresh...');
        const resp = await refreshClient.post(`${config.apiUrl}/api/authentication/refresh`, { refresh_token: refreshToken }, { headers: { 'Content-Type': 'application/json' } });
        const newAccessToken = resp?.data?.token;
        if (!newAccessToken) throw new Error('No access token from refresh');
        setCookie('_AT', newAccessToken);
        console.log('[Auth] Token refreshed after 401, retrying original request');

        // Update auth header and retry original
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axios(originalRequest);
      } catch (e) {
        // Propagate original error if refresh fails
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  //<React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  //</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
